import React, { useState, useEffect } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";

import { apiGet } from "../../../api/server"

const EVENT_TYPES = {
	5: "Face to face networking",
	6: "Sweat your BB membership",
	7: "Speed networking",
	8: "Weekly networking",
	9: "LinkedIn pods",
}
const LINKEDIN_POD_TYPE = 9

function ordinal(n) {
	const s = ["th", "st", "nd", "rd"]
	const v = n % 100
	return n + (s[(v - 20) % 10] || s[v] || s[0])
}

function getISOWeek(date) {
	const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
	const dayNum = d.getUTCDay() || 7
	d.setUTCDate(d.getUTCDate() + 4 - dayNum)
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
	return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
}

// Monday-Sunday bounds of the calendar week containing `date`.
function getWeekBounds(date) {
	const day = date.getDay() || 7 // Mon=1 ... Sun=7
	const monday = new Date(date)
	monday.setHours(0, 0, 0, 0)
	monday.setDate(date.getDate() - day + 1)
	const sunday = new Date(monday)
	sunday.setDate(monday.getDate() + 6)
	return { monday, sunday }
}

function formatWeekText(referenceDate) {
	if (!referenceDate) return "Week no. / start date - end date"
	const { monday, sunday } = getWeekBounds(referenceDate)
	const week = getISOWeek(monday)
	return `Week ${week} / ${formatDateRangeText(monday, sunday)} ${sunday.getFullYear()}`
}

function timeOf(date) {
	return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
}

function formatEventDateText(event) {
	const start = new Date(event.starttime)
	const weekday = start.toLocaleDateString("en-GB", { weekday: "long" })
	if (!event.endtime) return `${weekday} ${timeOf(start)}`
	const end = new Date(event.endtime)
	return `${weekday} ${timeOf(start)} - ${timeOf(end)}`
}

function formatMergedPodDateText(eventA, eventB) {
	const startA = new Date(eventA.starttime)
	const startB = new Date(eventB.starttime)
	const [first, second] = startA <= startB ? [eventA, eventB] : [eventB, eventA]
	const weekdayFirst = new Date(first.starttime).toLocaleDateString("en-GB", { weekday: "long" })
	const weekdaySecond = new Date(second.starttime).toLocaleDateString("en-GB", { weekday: "long" })
	const start = new Date(first.starttime)
	const end = first.endtime ? new Date(first.endtime) : null
	const timeText = end ? `${timeOf(start)} - ${timeOf(end)}` : timeOf(start)
	return `${weekdayFirst} & ${weekdaySecond}, ${timeText} to post your link`
}

function formatDateRangeText(start, end) {
	const startMonth = start.toLocaleDateString("en-GB", { month: "long" })
	const endMonth = end.toLocaleDateString("en-GB", { month: "long" })
	if (startMonth === endMonth) return `${ordinal(start.getDate())} - ${ordinal(end.getDate())} ${endMonth}`
	return `${ordinal(start.getDate())} ${startMonth} - ${ordinal(end.getDate())} ${endMonth}`
}

function toPrefill(dateText, event) {
	return {
		date: dateText,
		info: event.emailblurb || event.description || "",
		buttonText: event.linktext || "Register",
		buttonLink: event.url || "https://member-dashboard.findabusinessexpert.com/events",
		image: event.img || "https://business-butler-media.s3.eu-west-2.amazonaws.com/email-images/placeholder.png",
	}
}

const EventsDbPanel = (props) => {

	const [events, setEvents] = useState([])
	const [loading, setLoading] = useState(true)
	const [typeFilter, setTypeFilter] = useState("all")
	const [selectedIds, setSelectedIds] = useState([]) // ordered array of event ids
	const [fields, setFields] = useState({}) // id -> editable {date, info, buttonText, buttonLink, image}

	useEffect(() => {
		apiGet(`intranetevents/getallevents`).then(res => {
			res.json().then(data => {
				const now = new Date()
				const upcoming = (data.results || []).filter(event => new Date(event.starttime) > now)
				upcoming.sort((a, b) => new Date(a.starttime) - new Date(b.starttime))
				setEvents(upcoming)
				setLoading(false)
			})
		})
	}, [])

	const visibleEvents = typeFilter === "all" ? events : events.filter(e => String(e.eventtype) === typeFilter)

	function toggleSelect(event) {
		setSelectedIds(prev => {
			if (prev.includes(event.id)) {
				return prev.filter(id => id !== event.id)
			}
			setFields(f => ({ ...f, [event.id]: toPrefill(formatEventDateText(event), event) }))
			// getallevents returns a trimmed record; fetch the full event so emailblurb/description populate.
			apiGet(`intranetevents/geteventbyid/?id=${event.id}`).then(res => res.json()).then(data => {
				const fullEvent = data.results?.[0]
				if (!fullEvent) return
				setFields(f => ({ ...f, [event.id]: toPrefill(formatEventDateText(event), fullEvent) }))
			})
			return [...prev, event.id]
		})
	}

	function moveSelected(id, direction) {
		setSelectedIds(prev => {
			const index = prev.indexOf(id)
			const swapWith = index + direction
			if (swapWith < 0 || swapWith >= prev.length) return prev
			const next = [...prev]
			;[next[index], next[swapWith]] = [next[swapWith], next[index]]
			return next
		})
	}

	function updateField(id, key, value) {
		setFields(prev => ({ ...prev, [id]: { ...prev[id], [key]: value } }))
	}

	function getEventById(id) {
		return events.find(e => e.id === id)
	}

	function handleGenerate() {
		const selectedEvents = selectedIds.map(getEventById).filter(Boolean)

		// Bucket selected events by calendar week FIRST — pairing and pod-merging must
		// never cross a week boundary, each week is packed independently.
		const weekBuckets = []
		selectedIds.forEach(id => {
			const event = getEventById(id)
			if (!event) return
			const weekNum = getISOWeek(new Date(event.starttime))
			let bucket = weekBuckets[weekBuckets.length - 1]
			if (!bucket || bucket.week !== weekNum) {
				bucket = { week: weekNum, ids: [] }
				weekBuckets.push(bucket)
			}
			bucket.ids.push(id)
		})

		const finalGroups = weekBuckets.map(bucket => {
			const bucketEvents = bucket.ids.map(getEventById)

			// Auto-merge exactly-two LinkedIn pod events within this week into one row.
			const podEvents = bucketEvents.filter(e => e.eventtype === LINKEDIN_POD_TYPE)
			const mergePods = podEvents.length === 2
			const usedPodIds = new Set(mergePods ? [podEvents[0].id, podEvents[1].id] : [])

			const rowSlots = []
			let pendingPodMerge = null
			bucket.ids.forEach(id => {
				const event = getEventById(id)
				if (mergePods && usedPodIds.has(id)) {
					if (!pendingPodMerge) {
						pendingPodMerge = event
						return
					}
					const merged = toPrefill(formatMergedPodDateText(pendingPodMerge, event), event)
					rowSlots.push({ single: true, prefill: merged })
					pendingPodMerge = null
					return
				}
				rowSlots.push({ single: false, prefill: fields[id] || toPrefill(formatEventDateText(event), event) })
			})

			// Pack into dual/single rows, always breaking to a standalone single row
			// around anything flagged single (currently just the merged pod row).
			const rows = []
			let buffer = null
			rowSlots.forEach(slot => {
				if (slot.single) {
					if (buffer) {
						rows.push({ kind: "single", a: buffer })
						buffer = null
					}
					rows.push({ kind: "single", a: slot.prefill })
					return
				}
				if (buffer) {
					rows.push({ kind: "dual", a: buffer, b: slot.prefill })
					buffer = null
				} else {
					buffer = slot.prefill
				}
			})
			if (buffer) rows.push({ kind: "single", a: buffer })

			const { monday, sunday } = getWeekBounds(new Date(bucketEvents[0].starttime))
			return {
				rows,
				eventCount: bucketEvents.length,
				dateRangeText: formatDateRangeText(monday, sunday),
			}
		})

		const firstEventDate = selectedEvents.length ? new Date(selectedEvents[0].starttime) : null
		props.onGenerate(finalGroups, formatWeekText(firstEventDate))
	}

	return (
		<Container style={{ border: "1px solid #ccc", borderRadius: "4px", padding: "10px", marginBottom: "15px" }}>
			<Row>
				<Col>
					<h4>Generate Events Email from Database</h4>
				</Col>
			</Row>
			{loading ? <Row><Col>Loading events...</Col></Row> : <>
				<Row>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Filter by type</Form.Label>
							<Form.Control as="select" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
								<option value="all">All upcoming</option>
								{Object.entries(EVENT_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
							</Form.Control>
						</Form.Group>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<h5>Upcoming events ({visibleEvents.length})</h5>
						<div style={{ maxHeight: "250px", overflowY: "auto" }}>
							<Table size="sm" striped>
								<tbody>
									{visibleEvents.map(event => (
										<tr key={event.id}>
											<td><Form.Check type="checkbox" checked={selectedIds.includes(event.id)} onChange={() => toggleSelect(event)} /></td>
											<td>{event.title}</td>
											<td>{new Date(event.starttime).toLocaleString("en-GB")}</td>
											<td>{EVENT_TYPES[event.eventtype] || ""}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					</Col>
					<Col md={6}>
						<h5>Selected, in email order ({selectedIds.length})</h5>
						<div style={{ maxHeight: "400px", overflowY: "auto" }}>
							{selectedIds.map((id, index) => {
								const event = getEventById(id)
								if (!event) return null
								const field = fields[id] || toPrefill(formatEventDateText(event), event)
								return (
									<Container key={id} style={{ border: "1px solid #eee", marginBottom: "8px", padding: "6px" }}>
										<Row>
											<Col md={8}><strong>{event.title}</strong></Col>
											<Col md={4} style={{ textAlign: "right" }}>
												<Button size="sm" onClick={() => moveSelected(id, -1)} disabled={index === 0}>↑</Button>{" "}
												<Button size="sm" onClick={() => moveSelected(id, 1)} disabled={index === selectedIds.length - 1}>↓</Button>{" "}
												<Button size="sm" variant="danger" onClick={() => toggleSelect(event)}>Remove</Button>
											</Col>
										</Row>
										<Form.Group>
											<Form.Label>Date / time text</Form.Label>
											<Form.Control size="sm" value={field.date} onChange={(e) => updateField(id, "date", e.target.value)} />
										</Form.Group>
										<Form.Group>
											<Form.Label>Info</Form.Label>
											<Form.Control size="sm" as="textarea" rows={2} value={field.info} onChange={(e) => updateField(id, "info", e.target.value)} />
										</Form.Group>
										<Form.Group>
											<Form.Label>Button text</Form.Label>
											<Form.Control size="sm" value={field.buttonText} onChange={(e) => updateField(id, "buttonText", e.target.value)} />
										</Form.Group>
										<Form.Group>
											<Form.Label>Button link</Form.Label>
											<Form.Control size="sm" value={field.buttonLink} onChange={(e) => updateField(id, "buttonLink", e.target.value)} />
										</Form.Group>
										<Form.Group>
											<Form.Label>Image URL</Form.Label>
											<Form.Control size="sm" value={field.image} onChange={(e) => updateField(id, "image", e.target.value)} />
										</Form.Group>
									</Container>
								)
							})}
						</div>
						{selectedIds.filter(id => getEventById(id)?.eventtype === LINKEDIN_POD_TYPE).length === 2 &&
							<p><em>2 LinkedIn pod events selected — these will be combined into a single row automatically.</em></p>}
						<Button onClick={handleGenerate} disabled={selectedIds.length === 0}>Generate Events Template</Button>
					</Col>
				</Row>
			</>}
		</Container>
	)
}

export default EventsDbPanel;
